import type {
  AreaColumnUnit,
  CsvImportPreviewResponse,
} from "@inrange/building-manager-api-client";
import React from "react";
import { Button, Form } from "react-bootstrap";

// The two questions the import preview asks before it will run: which of the
// operator's columns is which, and what unit an area column the BE could not
// resolve is in. Both were inlined in the preview modal, which is how one
// Modal.Body reached several hundred lines.

// Operator-facing label for an area column key the BE couldn't unit-resolve.
// The BE returns its normalised key (`building_area_gia`); show it closer to
// how it appeared in the sheet.
const humanizeColumnKey = (key: string): string =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Operator-facing labels for the canonical keys a source column can be
// mapped onto. Keys not listed fall back to the humanized key.
const TARGET_LABEL: Record<string, string> = {
  site_name_or_id: "Site name / Site ID",
  full_address: "Full address",
  postcode: "Postcode",
  occupier_name: "Occupier name",
  building_area_sqm: "Building area (sqm)",
  building_area_sqft: "Building area (sqft)",
  number_of_buildings: "Number of buildings",
  annual_kwh: "Annual electricity (kWh)",
  building_use: "Building use",
  hh_data_filename: "Half hourly demand data filename",
};

const AREA_UNIT_OPTIONS: { value: AreaColumnUnit; label: string }[] = [
  { value: "sqm", label: "Square metres" },
  { value: "sqft", label: "Square feet" },
  { value: "ignore", label: "Don't use it" },
];

export const ColumnMappingPanel: React.FC<{
  // Required targets the BE could not find a column for. Non-empty turns the
  // panel red and blocks the import.
  missingRequiredColumns: string[];
  mappableTargets: string[];
  availableColumns: CsvImportPreviewResponse["availableColumns"];
  // The source column currently chosen for each target, already resolved
  // against what the BE detected. A plain map rather than a lookup function, so
  // nothing here can read a caller's binding before it exists.
  selectionByTarget: Record<string, string>;
  onSelectSource: (target: string, source: string) => void;
  applyDisabled: boolean;
  // Why Apply is disabled, or undefined when it is not.
  applyHint: string | undefined;
  applying: boolean;
  onApply: () => void;
}> = ({
  missingRequiredColumns,
  mappableTargets,
  availableColumns,
  selectionByTarget,
  onSelectSource,
  applyDisabled,
  applyHint,
  applying,
  onApply,
}) => (
  <div
    style={{
      border:
        missingRequiredColumns.length > 0
          ? "1px solid #f5c2c7"
          : "1px solid #ddd",
      background: missingRequiredColumns.length > 0 ? "#fff5f5" : "#fafafa",
      borderRadius: 6,
      padding: 12,
      marginBottom: 12,
    }}
  >
    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
      Column mapping
    </div>
    <Form.Text
      muted
      style={{ fontSize: 11, display: "block", marginBottom: 10 }}
    >
      {missingRequiredColumns.length > 0 ? (
        <>
          We couldn&rsquo;t work out which of your columns{" "}
          <strong>
            {missingRequiredColumns
              .map((t) => TARGET_LABEL[t] ?? humanizeColumnKey(t))
              .join(" and ")}
          </strong>{" "}
          {missingRequiredColumns.length === 1 ? "is" : "are"}. Choose{" "}
          {missingRequiredColumns.length === 1 ? "it" : "them"} below and the
          import can go ahead.
        </>
      ) : (
        <>
          This is how we read your file. Change anything we got wrong, then
          apply.
        </>
      )}
    </Form.Text>
    {mappableTargets.map((target) => {
      const selected = selectionByTarget[target] ?? "";
      const isRequiredAndMissing = missingRequiredColumns.includes(target);
      return (
        <div
          key={target}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <div style={{ fontSize: 13, minWidth: 170 }}>
            {TARGET_LABEL[target] ?? humanizeColumnKey(target)}
            {isRequiredAndMissing && (
              <span style={{ color: "#b02a37" }}> *</span>
            )}
          </div>
          <Form.Select
            size="sm"
            value={selected}
            isInvalid={isRequiredAndMissing && !selected}
            onChange={(e) => onSelectSource(target, e.target.value)}
          >
            <option value="">
              {isRequiredAndMissing ? "— choose a column —" : "— not used —"}
            </option>
            {availableColumns.map((col) => (
              <option key={col.key} value={col.key}>
                {col.label}
                {col.sampleValues.length > 0
                  ? ` (${col.sampleValues.join(", ")})`
                  : ""}
              </option>
            ))}
          </Form.Select>
        </div>
      );
    })}
    {/* An outline button at Bootstrap's disabled opacity (.65) is
        still mostly blue on white, so it reads as clickable. Swap
        to a filled grey when it isn't, which is unmistakable at a
        glance rather than a subtle fade. */}
    <Button
      size="sm"
      variant={applyDisabled ? "secondary" : "outline-primary"}
      style={
        applyDisabled
          ? {
              marginTop: 4,
              background: "#e9ecef",
              borderColor: "#dee2e6",
              color: "#adb5bd",
              opacity: 1,
              cursor: "not-allowed",
            }
          : { marginTop: 4 }
      }
      disabled={applyDisabled}
      title={applyHint}
      onClick={onApply}
    >
      {applying ? "Applying…" : "Apply mapping"}
    </Button>
  </div>
);

export const AreaUnitPanel: React.FC<{
  // Area columns whose header does not say what unit they are in. Empty
  // renders nothing.
  columns: CsvImportPreviewResponse["unitAmbiguousAreaColumns"];
  units: Record<string, AreaColumnUnit>;
  onSelectUnit: (columnKey: string, unit: AreaColumnUnit) => void;
}> = ({ columns, units, onSelectUnit }) => {
  if (columns.length === 0) return null;
  return (
    <div
      style={{
        border: "1px solid #ffe69c",
        background: "#fff9e6",
        borderRadius: 6,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          marginBottom: 6,
        }}
      >
        What unit {columns.length === 1 ? "is" : "are"}{" "}
        {columns.length === 1 ? "this column" : "these columns"} in?
      </div>
      <Form.Text
        muted
        style={{ fontSize: 11, display: "block", marginBottom: 10 }}
      >
        The header doesn&rsquo;t say, and square metres and square feet differ
        by 10.8x - too close to a genuine multi-storey building for us to tell
        them apart afterwards. Building area drives which polygon we match, so
        we&rsquo;d rather ask than guess.
      </Form.Text>
      {columns.map((col) => (
        <div key={col.key} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 13 }}>
            <strong>{humanizeColumnKey(col.key)}</strong>{" "}
            <span style={{ color: "#888", fontSize: 11 }}>
              ({col.rowsPopulated} row
              {col.rowsPopulated === 1 ? "" : "s"}
              {col.sampleValues.length > 0 && (
                <>, e.g. {col.sampleValues.join(", ")}</>
              )}
              )
            </span>
          </div>
          <div style={{ marginTop: 2 }}>
            {AREA_UNIT_OPTIONS.map((opt) => (
              <Form.Check
                key={opt.value}
                inline
                type="radio"
                label={opt.label}
                name={`areaUnit-${col.key}`}
                id={`areaUnit-${col.key}-${opt.value}`}
                checked={units[col.key] === opt.value}
                onChange={() => onSelectUnit(col.key, opt.value)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
